import { ReactElement, ReactNode, useEffect, useCallback, useId } from 'react';
import { PencilIcon, CheckIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { IconButton } from '@/components/ui/IconButton';
import { Spinner } from '@/components/ui/Spinner';

interface InlineEditRowProps {
    /** Whether this row is currently in edit mode. */
    isEditing: boolean;
    /** Callback when edit button is clicked. */
    onEdit: () => void;
    /** Callback when save button is clicked. */
    onSave: () => void;
    /** Callback when cancel button is clicked. */
    onCancel: () => void;
    /** Callback when delete button is clicked. */
    onDelete: () => void;
    /** Content to display in view mode. */
    viewContent: ReactNode;
    /** Content to display in edit mode (form fields). */
    editContent: ReactNode;
    /** Title to display in edit mode header (e.g., "Edit value"). */
    editTitle?: string;
    /** Whether save action is in progress. */
    isSaving?: boolean;
    /** Whether delete action is in progress. */
    isDeleting?: boolean;
    /** Whether delete is disabled (e.g., cannot delete last item). */
    deleteDisabled?: boolean;
    /** Tooltip text when delete is disabled. */
    deleteDisabledReason?: string;
    /** Additional CSS classes for the row container. */
    className?: string;
}

/**
 * Inline editable row component that switches between view and edit modes.
 * In view mode, displays content with an edit button on the right.
 * In edit mode, displays form fields with save/delete on the left, close button on the right
 * (same position as edit button was).
 * Parent component controls which row is being edited (single edit at a time).
 */
export function InlineEditRow({
    isEditing,
    onEdit,
    onSave,
    onCancel,
    onDelete,
    viewContent,
    editContent,
    editTitle,
    isSaving = false,
    isDeleting = false,
    deleteDisabled = false,
    deleteDisabledReason,
    className = '',
}: InlineEditRowProps): ReactElement {
    const isLoading = isSaving || isDeleting;
    const viewContentId = useId();

    // Handle keyboard shortcuts when in edit mode
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (isLoading) return;

            if (e.key === 'Escape') {
                e.preventDefault();
                onCancel();
            } else if (e.key === 'Enter') {
                // Leave Enter to textareas, contenteditable and focused buttons.
                const target = e.target as HTMLElement;
                if (
                    target.tagName === 'TEXTAREA' ||
                    target.tagName === 'BUTTON' ||
                    target.isContentEditable
                ) {
                    return;
                }
                e.preventDefault();
                onSave();
            }
        },
        [isLoading, onCancel, onSave]
    );

    useEffect(() => {
        if (!isEditing) return;

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isEditing, handleKeyDown]);

    if (isEditing) {
        return (
            <div
                data-testid="inline-edit-row"
                className={`border border-accent dark:border-primary-light rounded-lg bg-active-light dark:bg-active-dark ring-1 ring-accent/20 dark:ring-primary-light/20 ${className}`}
            >
                {/* Top row - the title button covers the whole row and collapses it */}
                <div className="relative flex items-center justify-between px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-800/50 rounded-t-lg">
                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label={editTitle ? undefined : 'Collapse editor'}
                        className="min-w-0 flex-1 cursor-pointer self-stretch text-left font-medium text-gray-700 dark:text-gray-200 focus:outline-hidden after:absolute after:inset-0 after:rounded-t-lg focus-visible:after:ring-2 focus-visible:after:ring-accent dark:focus-visible:after:ring-primary-light"
                    >
                        {editTitle}
                    </button>
                    <div className="relative flex items-center gap-2">
                        <IconButton
                            variant="accent"
                            size="md"
                            onClick={onSave}
                            disabled={isLoading}
                            aria-label="Save changes"
                            title="Save"
                        >
                            {isSaving ? (
                                <Spinner />
                            ) : (
                                <CheckIcon className="h-5 w-5" />
                            )}
                        </IconButton>
                        <IconButton
                            variant="danger"
                            size="md"
                            onClick={onDelete}
                            disabled={isLoading || deleteDisabled}
                            aria-label="Delete item"
                            title={deleteDisabled ? deleteDisabledReason : 'Delete'}
                        >
                            {isDeleting ? (
                                <Spinner />
                            ) : (
                                <TrashIcon className="h-5 w-5" />
                            )}
                        </IconButton>
                        <IconButton
                            variant="default"
                            size="md"
                            onClick={onCancel}
                            disabled={isLoading}
                            aria-label="Cancel editing"
                            title="Cancel"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </IconButton>
                    </div>
                </div>

                {/* Edit form content */}
                <div className="px-4 pb-4 pt-2 border-t border-gray-200 dark:border-gray-700">
                    {editContent}
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="inline-edit-row"
            className={`relative border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-secondary hover:border-gray-300 dark:hover:border-gray-600 transition-colors ${className}`}
        >
            {/* Row button - covers the whole row, named by the view content */}
            <button
                type="button"
                onClick={onEdit}
                aria-labelledby={viewContentId}
                className="absolute inset-0 cursor-pointer rounded-lg focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent dark:focus-visible:ring-primary-light"
            />
            <div className="flex items-center justify-between px-4 py-3">
                {/* View content - sits above the row button so its tooltips still show */}
                <div
                    id={viewContentId}
                    className="relative flex-1 min-w-0 cursor-pointer"
                    onClick={onEdit}
                >
                    {viewContent}
                </div>

                {/* Edit button - position is matched by close button in edit mode */}
                <IconButton
                    variant="default"
                    size="md"
                    onClick={onEdit}
                    aria-label="Edit item"
                    title="Edit"
                    className="relative ml-2 shrink-0"
                >
                    <PencilIcon className="h-5 w-5" />
                </IconButton>
            </div>
        </div>
    );
}
